import CardFrom, { CardFormConfig } from '../../component/card/card-from';
import DialogComponent from '../../component/dialog/dialog.component';
import i18n from '../../i18n';
import CardView from '../../component/card/card-view';
import { OperationType, SensitiveRecord } from '../../models/sensitive-record.entity';
import { Box, Typography } from '@mui/material';
import TableComponent, { HeadCell } from '../../component/table/table.component';
import { Reseller, Staff } from '../../models';
import { AuthCard } from '../group/authorization-manager/auth-card';
import ResellerAccount from '../reseller/reseller-account.page';
import { ResellerAccountState } from '../reseller/reseller-account-state';
import { getAuthPointI18n } from '../../constants/tool-auth-point';

export default function SensitiveOperationsDetail(props: Readonly<{
    isOpen: boolean;
    onClose: () => void;
    detail: SensitiveRecord;
}>) {
    // 员工账号
    const staffInfo: CardFormConfig[] = [
        {
            key: 'baseInfo',
            title: i18n.t('ground_staff_component_text_basic_information'),
            rowSpacing: 6,
            columnSpacing: { md: 4, xs: 4 },
            children: [
                {
                    comType: 'AVATAR',
                    key: 'avatar',
                    xs: 12,
                    isDisabledUpload: true
                },
                {
                    key: 'first_name',
                    label: i18n.t('responsible_person_info_component_text_name'),
                    regularType: 'inputLimit',
                    comType: 'INPUT',
                    inputType: 'INPUT',
                    xs: 12,
                    md: 6,
                    disabledInput: true,
                },
                {

                    key: 'last_name',
                    label: i18n.t('person_page_text_surname'),
                    regularType: 'inputLimit',
                    comType: 'INPUT',
                    inputType: 'INPUT',
                    xs: 12,
                    md: 6,
                    disabledInput: true,
                },
                {
                    key: 'gender',
                    label: i18n.t('general_text_gender'),
                    comType: 'INPUT',
                    inputType: 'SELECT',
                    items: [{
                        value: '0',
                        label: i18n.t('person_page_button_male')
                    }, {
                        value: '1',
                        label: i18n.t('person_page_button_female')
                    }],
                    xs: 12,
                    md: 6,
                    disabledInput: true,
                },
                {
                    key: 'birth',
                    label: i18n.t('person_page_text_birthday'),
                    comType: 'INPUT',
                    inputType: 'DATE',
                    handleChangePicker: () => { },
                    xs: 12,
                    md: 6,
                    maxDate: new Date(),
                    disabledInput: true,
                },
            ]
        },
        {
            key: 'contact',
            title: i18n.t('restaurant_info_page_text_contact'),
            rowSpacing: 6,
            columnSpacing: { md: 4, xs: 4 },
            children: [
                {
                    key: 'phone_prefix',
                    label: i18n.t('general_text_country_code'),
                    comType: 'INPUT',
                    inputType: 'COUNTRY_SELECT',
                    xs: 12,
                    md: 3,
                    disabledInput: true,
                },
                {
                    key: 'phone',
                    label: i18n.t('general_text_phone'),
                    comType: 'INPUT',
                    inputType: 'INPUT',
                    xs: 12,
                    md: 9,
                    disabledInput: true,
                },
                {
                    key: 'email',
                    label: i18n.t('general_text_email'),
                    comType: 'INPUT',
                    inputType: 'INPUT',
                    xs: 12,
                    md: 12,
                    disabledInput: true,
                }
            ]
        }
    ];

    // 员工账号修改内容高亮
    const editStaffInfo = staffInfo.map((item) => ({
        ...item,
        children: item.children?.map((childItem) => ({
            ...childItem,
            inputIsEdited: props.detail?.details?.new_staff_info?.[childItem.key] !== props.detail?.details?.origin_staff_info?.[childItem.key]
        }))
    }))


    // 餐厅表格头部
    const restaurantTableHeadCell: HeadCell[] = [
        {
            id: 'belonged_group',
            label: i18n.t('group_management_page_text_group_identification_code'),
        },
        {
            id: 'nickname',
            label: i18n.t('restaurant_info_page_text_restaurant_name'),
        },
        {
            id: 'string_id',
            label: i18n.t('group_management_page_text_restaurant_identification_code'),
        },
        {
            id: 'staff',
            label: i18n.t('general_table_text_staff_name'),
            renderFunction: (row: Staff) => `${row.first_name ?? ''} ${row.last_name ?? ''}`
        }
    ]

    // gyb设备表格头部
    const gybTableHeadCell: HeadCell[] = [
        { id: 'location_id', label: i18n.t('gyb_page_text_registered_id') },
        { id: 'operator', label: i18n.t('gyb_page_text_operator') },
        { id: 'license_code', label: i18n.t('gyb_page_text_GYB_key') },
        { id: 'device_code', label: i18n.t('gyb_page_text_the_device_number') },
        {
            id: 'is_default', label: i18n.t('gyb_page_text_default_device'),
            renderFunction: (row: any,) => {
                return row.is_default === 1 ? i18n.t('general_text_yes') : i18n.t('general_text_no')
            }
        },
    ]

    // 密钥表格
    const secretKeyTableHeadCell: HeadCell[] = [
        {
            id: 'name',
            label: i18n.t('responsible_person_info_component_text_name')
        },
        {
            id: 'value',
            label: i18n.t('restaurant_extensions_page_text_key'),
            renderFunction: (row) => {
                return (
                    <Typography sx={{ wordBreak: 'break-all' }} >
                        {row?.value}
                    </Typography>)
            }
        },
    ]

    // 更改集团
    const replaceGroupTableHeadCell: HeadCell[] = [
        {
            id: 'oldGroupstring_id',
            label: `${i18n.t('group_management_page_text_group_identification_code')} ${(i18n.t('general_text_before_edit'))}`,
        },
        {
            id: 'oldGroupnickname',
            label: `${i18n.t('group_management_page_text_group_name')} ${(i18n.t('general_text_before_edit'))}`,
        },
        {
            id: 'newGroupstring_id',
            label: `${i18n.t('group_management_page_text_group_identification_code')} ${(i18n.t('general_text_after_edit'))}`,
        },
        {
            id: 'newGroupnickname',
            label: `${i18n.t('group_management_page_text_group_name')} ${(i18n.t('general_text_after_edit'))}`,
        },
    ]

    // 云分析数据来源变更详情表格头部
    const cloudAnalysisOriginTableHeadCell: HeadCell[] = [
        {
            id: 'previous_value',
            label: i18n.t('sensitive_operation_cloud_analysis_origin_previous_value'),
        },
        {
            id: 'new_value',
            label: i18n.t('sensitive_operation_cloud_analysis_origin_new_value'),
        },
        {
            id: 'changed',
            label: i18n.t('sensitive_operation_cloud_analysis_origin_changed'),
            renderFunction: (row: SensitiveRecord['details']) => {
                if (row?.changed === undefined) {
                    return '';
                }
                return row.changed ? i18n.t('general_text_yes') : i18n.t('general_text_no');
            },
        },
        {
            id: 'operator_account_type',
            label: i18n.t('sensitive_operation_cloud_analysis_origin_operator_account_type'),
        },
        {
            id: 'request_path',
            label: i18n.t('sensitive_operation_cloud_analysis_origin_request_path'),
        },
    ];

    /**
     * 渲染组件
     * @returns
     */
    const renderDetail = () => {
        const type = props.detail?.operation_type;
        // 绑定、解绑餐厅数据
        const detail = {
            ...props.detail?.details?.staff,
            ...props.detail?.details?.restaurant
        }
        // 授权包授权点
        const warrants = props.detail?.warrants?.[0]?.warrants ? props.detail?.warrants?.[0]?.warrants : props.detail?.warrants;

        // 处理新增、编辑、删除经销商
        const { useResellerAccountData, useResellerAccountEditDataNew, useResellerAccountEditDataOrigin } = editResellerInfo()

        switch (type) {
            case OperationType.OP_Add_Staff:
            case OperationType.OP_Delete_Staff:
            case OperationType.OP_Update_Password:
                return (
                    <CardFrom
                        config={staffInfo}
                        data={props.detail?.details?.staff_info || {}}
                    />
                )
            case OperationType.OP_Update_Staff:
                return (
                    <>
                        <CardView
                            title={i18n.t('general_text_before_edit')}
                            contentRender={(
                                <CardFrom
                                    config={editStaffInfo}
                                    data={props.detail?.details?.origin_staff_info || {}}
                                />
                            )}
                        />
                        <CardView
                            title={i18n.t('general_text_after_edit')}
                            contentRender={(
                                <CardFrom
                                    config={editStaffInfo}
                                    data={props.detail?.details?.new_staff_info || {}}
                                />
                            )}
                        />
                    </>
                )
            case OperationType.OP_ADD_WARRANT:
            case OperationType.OP_CANCEL_WARRANT:
                return (
                    <Box display='flex' flexWrap='wrap'>
                        {warrants?.map((wararntItem, wararntItemIndex) => {
                            // 筛选出对应国际化授权名字
                            const filterI18nName = getAuthPointI18n(wararntItem?.warranti18N?.name || []);
                            return <AuthCard
                                key={wararntItemIndex}
                                name={filterI18nName}
                                startDate={wararntItem?.limit_point?.start_date}
                                endDate={wararntItem?.limit_point?.end_date}
                                type='single'
                                showSelectBox={false}
                                selected={false}
                                onClick={() => { }}
                            />
                        })
                        }

                    </Box>
                )
            case OperationType.OP_Unbind_Restaurant:
            case OperationType.OP_Bind_Restaurant:
                return (
                    <TableComponent
                        {...{
                            showChecked: 0,
                            list: props.detail?.details?.restaurant ? [detail] : [],
                            headCellList: restaurantTableHeadCell || [],
                        }}
                    />

                )
            case OperationType.OP_Add_GybDevice:
            case OperationType.OP_Delete_GybDevice:
            case OperationType.OP_Default_GybDevice:
                return (
                    <TableComponent
                        {...{
                            showChecked: 0,
                            list: props.detail?.gyb_device ? [props.detail?.gyb_device] : [],
                            headCellList: gybTableHeadCell || [],
                        }}
                    />
                )
            case OperationType.OP_Add_SecretKey:
            case OperationType.OP_Delete_SecretKey:
            case OperationType.OP_Refresh_SecretKey:
                return (
                    <TableComponent
                        {...{
                            showChecked: 0,
                            list: props.detail?.secret_key ? [props.detail?.secret_key] : [],
                            headCellList: secretKeyTableHeadCell || [],
                        }}
                    />
                )
            case OperationType.OP_Replace_Group:
                return (
                    <TableComponent
                        {...{
                            showChecked: 0,
                            list: props.detail?.replace_group ? [props.detail?.replace_group] : [],
                            headCellList: replaceGroupTableHeadCell || [],
                        }}
                    />
                )
            case OperationType.OP_Update_Cloud_Analysis_Origin:
                return (
                    <TableComponent
                        {...{
                            showChecked: 0,
                            list: props.detail?.details ? [props.detail.details] : [],
                            headCellList: cloudAnalysisOriginTableHeadCell,
                        }}
                    />
                )
            case OperationType.OP_Add_Reseller:
            case OperationType.OP_Delete_Reseller:
                return (
                    <ResellerAccount
                        componentSource='SENSITIVE'
                        useResellerAccountMobx={useResellerAccountData}
                    />
                )
            case OperationType.OP_Update_Reseller:
                return (

                    <>
                        <CardView
                            title={i18n.t('general_text_before_edit')}
                            contentRender={(
                                <ResellerAccount
                                    componentSource='SENSITIVE'
                                    useResellerAccountMobx={useResellerAccountEditDataOrigin}
                                />
                            )}
                        />
                        <CardView
                            title={i18n.t('general_text_after_edit')}
                            contentRender={(
                                <ResellerAccount
                                    componentSource='SENSITIVE'
                                    useResellerAccountMobx={useResellerAccountEditDataNew}
                                />
                            )}
                        />
                    </>

                )
            default:
                return (
                    <>
                    </>
                )
        }
    }

    // 处理新增、编辑、删除经销商
    const editResellerInfo = () => {
        // 新增，删除经销商账号数据管理
        let useResellerAccountData;
        const resellerInfo = props.detail?.details?.reseller_info
        if (resellerInfo?._id) {
            useResellerAccountData = new ResellerAccountState({
                defaultResellerInfo: resellerInfo,
            });
        }
        // 编辑经销商账号数据管理
        let useResellerAccountEditDataNew;
        let useResellerAccountEditDataOrigin;
        const newResellerInfo = props.detail?.details?.new_reseller_info
        const originResellerInfo = props.detail?.details?.origin_reseller_info
        if (newResellerInfo?._id && originResellerInfo?._id) {
            const operateMap = organizeOperateMap(originResellerInfo, newResellerInfo)
            useResellerAccountEditDataNew = new ResellerAccountState({
                defaultResellerInfo: newResellerInfo,
                defaultOperateMap: operateMap
            });
            useResellerAccountEditDataOrigin = new ResellerAccountState({
                defaultResellerInfo: originResellerInfo,
                defaultOperateMap: operateMap
            });
        }
        return {
            useResellerAccountData,
            useResellerAccountEditDataNew,
            useResellerAccountEditDataOrigin
        }

    }

    /**
     * 修改的内容高亮
     * @param originResellerInfo 
     * @param newResellerInfo 
     * @returns 
     */
    const organizeOperateMap = (originResellerInfo: Reseller, newResellerInfo: Reseller) => {
        // 修改的内容高亮
        const operateMap: Map<string, { origin: number, current: number }> = new Map(
        );
        for (const key in originResellerInfo) {
            const origin = originResellerInfo[key];
            const current = newResellerInfo[key];
            if (origin !== current) {
                operateMap.set(key, { origin, current });
            }
        }

        return operateMap
    };


    return (
        <DialogComponent
            maxWidth='md'
            heightSize='medium'
            open={props.isOpen}
            defaultHeaderProps={{
                title: i18n.t('operation_record_operation_object_details'),
            }}
            onClose={props.onClose}
        >

            {renderDetail()}
        </DialogComponent>
    )
}